/** @jsxImportSource @emotion/react */
import React, {useMemo} from 'react'
import moment from 'moment-timezone'
import _ from 'lodash'
import { round } from 'utils'
import {
  Line,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  ReferenceArea,
} from 'recharts'
import { TooltipList, TooltipListItem, TooltipHeader, formatTimestamp } from './Tooltip'
import Legend from './Legend'
import { useSeries } from 'hooks'
import { InfoTip } from 'component\s'

const SERIES = generationType => ({
  'generation': {
    label: 'Actual generation',
    color: '#FFD52D',
  },
  'forecastGeneration': {
    label: 'Generation forecast',
    color: '#fff',
    tooltip: "Aggregated forecasted grid connected generation based on weather, available capacity and other factors",
  },
  'cleared': {
    label: `Cleared ${generationType} gen.`,
    color: '#CBE612',
    tooltip: `Aggregated cleared generation from ${generationType} farm generators`,
  },
  'shortfall': {
    label: 'Discrepancy',
    color: '#ED8B00',
    tooltip: "Forecasted generation minus cleared generation",
  },
})

const SERIES_OPTIONS = {
  xTicks: {every: 'hour', add: 4}
}

const CustomTooltip = ({ active, series, payload, generationType }) => {
  if (active && payload?.length && series?.length){
    const {timestamp, tradingPeriod, ...data} = payload[0].payload ?? {};
    return (
      <TooltipList>
        <TooltipHeader label={formatTimestamp(timestamp, {tradingPeriod})}/>
        { series.map((g, idx) => {
            const {label, color, strokeDasharray} = SERIES(generationType)[g]
            return (
              <TooltipListItem
                key={idx}
                label={label}
                color={color}
                strokeDasharray={strokeDasharray}
                value={`${round(data[g])} MW`}
              />
          )})
        }
      </TooltipList>
    )
  }
}


const IntermittentForecast = ({generationType, generationData, forecastData, width='100%', height='100%', zoomCallback, currentInterval=null, tooltips=[]}) => {

  const intermittentData = useMemo(() => [
      ...(generationData?.items?.slice()?.reverse() || []),
      ...(forecastData?.items || [])
    ].map(({timestamp, ...rest}) => ({...rest, timestamp: moment(timestamp).valueOf()})),
  [JSON.stringify(generationData), JSON.stringify(forecastData)])

  const series = SERIES(generationType)
  const [graph, actions] = useSeries(series, intermittentData, {...SERIES_OPTIONS, zoomCallback})
  const firstOffered = graph.data.find((item) => !!item.offered)?.timestamp
  const [xMin, xMax] = [graph.data[0]?.timestamp || 0, _.last(graph.data)?.timestamp || 0]
  const currentIntervalX = currentInterval && currentInterval.valueOf()

  return (
    <>
      <ResponsiveContainer width={width} height={height}>
        <ComposedChart
          data={graph.data}
          {...actions.chartProps}
        >
          <XAxis
            tick={{
              fill: 'white',
              fontSize: '14px',
            }}
            dataKey="timestamp"
            scale="time"
            type="number"
            domain={graph.xDomain}
            ticks={graph.xTicks}
            tickFormatter={tick => moment(tick).format('HH:mm')}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{
              fill: 'white',
              fontSize: '14px',
            }}
            type="number"
            axisLine={false}
            tickLine={false}
            scale={graph.yScale}
            domain={graph.yDomain}
          />

          <defs>
            <linearGradient id="currentIntervalGradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#B1E9E6" stopOpacity={0} />
              <stop offset="100%" stopColor="#B1E9E6" stopOpacity={0.4} />
            </linearGradient>
          </defs>

          {currentIntervalX && (currentIntervalX > xMin) && (currentIntervalX < xMax) &&
            <>
              <ReferenceArea x2={currentIntervalX} fill="url(#currentIntervalGradient)" />
              <ReferenceLine
                x={currentIntervalX}
                stroke="white"
              />
            </>
          }

          <Tooltip
            content={args => CustomTooltip({...args, series: graph.activeSeries, generationType})}
          />

          <defs>
            <linearGradient id="currentTimeGradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#B1E9E6" stopOpacity={0} />
              <stop offset="100%" stopColor="#B1E9E6" stopOpacity={0.18} />
            </linearGradient>
          </defs>

          <ReferenceArea x2={firstOffered} fill="url(#currentTimeGradient)" />
          <ReferenceLine x={firstOffered} stroke="#fff"/>
          <ReferenceLine y={0} stroke="#fff"/>

          {graph.visibleSeries.map(g =>
            <Line
              key={g}
              type="linear"
              dataKey={g}
              stroke={series[g].color}
              strokeDasharray={series[g].strokeDasharray}
              animationDuration={300}
              dot={false}
              strokeWidth={2}
              activeDot={actions.isSeriesActive(g)}
            />
          )}

          {actions.renderZoomOverlay()}

          <CartesianGrid stroke="#fff" opacity={0.25} strokeDasharray="3 3"/>
        </ComposedChart>
      </ResponsiveContainer>
      <InfoTip tooltips={tooltips}>
        Showing aggregated forecasted grid connected {generationType} generation against cleared generation from {generationType} farm generators
      </InfoTip>
      <Legend
        series={series}
        visible={graph.visibleSeries}
        onSeriesToggle={actions.setVisibleSeries}
        tooltips={tooltips}
      />
    </>
  )

}

export default IntermittentForecast